import _ from 'lodash';
import {match} from 'path-to-regexp';

import {addQueryToPath, QueryStringArguments} from '../../../libs/shared/helpers/src/browser/urlHelpers';
import {hasProperty, makeKeyOfType} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {getRegexpMatches} from '../../../libs/shared/helpers/src/utilities/stringHelpers';
import {isTeammateNamespace} from '../../helpers/models/namespaceHelpers';
import {CollapsedItemKeyPrefixesEnum} from '../../models/api/localPreferencesModel';
import {SettingsMenuItemTemplate} from './settingsMenuCommonTypes';

// Avoid using upper case letters in parameters, like :recipeId
const settingsPathsToUrlTemplateMap = Object.freeze({
  'settings.responder.list': '/settings/:namespace/responders',
  'settings.responder.edit': '/settings/:namespace/responders/edit/:id',
  'settings.responder.new': '/settings/:namespace/responders/new',
  'settings.answers': '/settings/:namespace/answers',
  'settings.answers.list': '/settings/:namespace/answers',
  'settings.answers.new': '/settings/:namespace/answers/new',
  'settings.answers.edit.overview': '/settings/:namespace/answers/:answer/edit',
  'settings.billing': '/settings/billing',
  'settings.circles': '/settings/:namespace/circles',
  'settings.circles.new': '/settings/:namespace/circles/new',
  'settings.circles.edit': '/settings/:namespace/circles/edit/:circle',
  'settings.custom-fields': `/settings/:namespace/custom-fields/:tabpage/overview`,
  'settings.custom-fields.new': `/settings/:namespace/custom-fields/:tabpage/new`,
  'settings.custom-fields.edit': `/settings/:namespace/custom-fields/:tabpage/edit/:customfield`,
  'settings.companyPreferences.general': '/settings/preferences/general',
  'settings.companyPreferences.sso': '/settings/preferences/sso',
  'settings.companyPreferences': '/settings/preferences',
  'settings.contactPreferences': '/settings/preferences',
  'settings.custom-views.edit': '/settings/:namespace/custom-views/edit/:customView',
  'settings.custom-views.new': '/settings/:namespace/custom-views/new',
  'settings.custom-views': '/settings/:namespace/custom-views',
  'settings.deliverability': '/settings/deliverability',
  'settings.deliverability.domain': '/settings/deliverability/:domain_id',
  'settings.home': '/settings/home',
  'settings.inboxes.delegations.edit': '/settings/:namespace/inboxes/delegations/edit/:delegation',
  'settings.inboxes.edit.channels.list': '/settings/:namespace/inboxes/edit/:inbox/channels/list',
  'settings.inboxes.edit.channels.channel': '/settings/:namespace/inboxes/edit/:inbox/channels/:channelId',
  'settings.inboxes.edit.overview': '/settings/:namespace/inboxes/edit/:inbox/overview',
  'settings.inboxes': '/settings/:namespace/inboxes',
  'settings.inboxes.new': '/settings/:namespace/inboxes/new',
  'settings.inboxes.list': '/settings/:namespace/inboxes',
  'settings.integrations.native.edit.details': '/settings/integrations/native/edit/:namespace/details',
  'settings.integrations.native.list': '/settings/integrations/native',
  'settings.integrations': '/settings/integrations',
  'settings.myPreferences.general': '/settings/:namespace/user-preferences/general',
  'settings.myPreferences': '/settings/:namespace/user-preferences',
  'settings.snoozes.list': '/settings/:namespace/snoozes',
  'settings.notifications': '/settings/notifications',
  'settings.profile.overview': '/settings/profile/overview',
  'settings.profile': '/settings/profile',
  'settings.roles.edit.overview': '/settings/:namespace/roles/edit/:role/overview',
  'settings.roles': '/settings/:namespace/roles',
  'settings.roles.new': '/settings/:namespace/roles/new',
  'settings.rules.recipes': '/settings/:namespace/rules/recipes/:recipeid',
  'settings.rules.recipecsat': '/settings/:namespace/rules/recipes/csat/:recipeid',
  'settings.rules.ui': '/settings/:namespace/rules',
  'settings.rules.ui.library': '/settings/:namespace/rules/library',
  'settings.rules.ui.edit': '/settings/:namespace/rules/edit/:ruleid',
  'settings.rules.ui.new': '/settings/:namespace/rules/new',
  'settings.rules.ui.limits.list': '/settings/:namespace/rules/limits',
  'settings.rules': '/settings/:namespace/rules',
  'settings.shifts': '/settings/:namespace/shifts',
  'settings.signatures.edit.overview': '/settings/:namespace/signatures/edit/:signature/overview',
  'settings.signatures': '/settings/:namespace/signatures',
  'settings.signatures.new': '/settings/:namespace/signatures/new',
  'settings.signatures.list': '/settings/:namespace/signatures',
  'settings.tags.edit': '/settings/:namespace/tags/edit/:tag',
  'settings.tags.new': '/settings/:namespace/tags/new',
  'settings.tags': '/settings/:namespace/tags',
  'settings.new-tags.edit': '/settings/:namespace/tags/:tag/edit',
  'settings.new-tags': '/settings/:namespace/tags/overview',
  'settings.teammates.edit.overview': '/settings/:namespace/teammates/edit/:teammate/overview',
  'settings.teammates.export': '/settings/:namespace/teammates/export',
  'settings.teammates.provisioning': '/settings/:namespace/teammates/provisioning',
  'settings.teammates': '/settings/:namespace/teammates',
  'settings.teams.edit': '/settings/teams/edit/:team',
  'settings.teams.list': '/settings/teams/list',
  'settings.teams': '/settings/teams',
  'settings.tools.imports.list': '/settings/tools/imports',
  'settings.tools.imports.new': '/settings/tools/imports/:importType',
  'settings.tools.plugins.edit': '/settings/tools/plugins/edit/:plugin',
  'settings.tools.plugins.list': '/settings/tools/plugins',
  'settings.tools': '/settings/tools',
  'settings.user-home': '/settings/user-home',
  'settings.userShifts': '/settings/my-shifts',
  'settings.teammates.new': '/settings/:namespace/teammates/new',
  'settings.teammates.list': '/settings/:namespace/teammates',
  'settings.teammates.new-ui': '/settings/:namespace/teammates/new-ui',
  'settings.applications': '/settings/applications',
  'settings.apps': '/settings/apps',
  'settings.developers': '/settings/developers',
});

export const RSettingsPathId = makeKeyOfType('SettingsPathId', settingsPathsToUrlTemplateMap);
export type SettingsPathId = keyof typeof settingsPathsToUrlTemplateMap;

const personalRoutesWithoutNamespace: ReadonlyArray<SettingsPathId> = Object.freeze([
  'settings.profile',
  'settings.myPreferences',
  'settings.notifications',
  'settings.notifications',
  'settings.userShifts',
  'settings.user-home',
]);

// Navigating to the Rule Library with the below search key will result in a pre-filled search.
export const ruleLibrarySearchKey = 'rulelibrarysearch';

const makeMatchMethodForPathId = _.memoize((pathId: SettingsPathId) =>
  match(settingsPathsToUrlTemplateMap[pathId], {end: false}),
);

const parametersRegex = new RegExp(':([a-z]+)', 'g');
export function makeSettingsUrlFromPathId(
  pathId: SettingsPathId,
  parameters?: object,
  query?: QueryStringArguments,
) {
  const urlTemplate = settingsPathsToUrlTemplateMap[pathId];

  if (!urlTemplate) {
    throw new Error(`Unknown pathId ${pathId}`);
  }

  if (!parameters) {
    return urlTemplate;
  }

  const parametersInTemplate = getRegexpMatches(parametersRegex, urlTemplate);

  const baseUrl = _(parametersInTemplate)
    .map((a) => a[1])
    .compact()
    .reduce((finalString, paramMatch) => {
      if (!hasProperty(parameters, paramMatch)) {
        return finalString;
      }

      const parameterValue = String(parameters[paramMatch] || '');

      return finalString.replace(`:${paramMatch}`, parameterValue);
    }, urlTemplate);

  return addQueryToPath(baseUrl, query);
}

export function findCurrentSettingsPathId(locationPathAndQuery: string) {
  return Object.keys(settingsPathsToUrlTemplateMap).find((pathId) =>
    makeMatchMethodForPathId(pathId as SettingsPathId)(locationPathAndQuery),
  ) as SettingsPathId | undefined;
}

export function isSettingsPathItemMatchingPathId(item: SettingsMenuItemTemplate, pathId: SettingsPathId) {
  return item.pathId === pathId || item.pathId.startsWith(pathId);
}

export function locationPathAndQueryIncludesPathId(locationPathAndQuery: string, pathId: SettingsPathId) {
  const item = makeSettingsItemFromLocationPathAndQuery(locationPathAndQuery);

  if (!item) {
    return undefined;
  }

  return isSettingsPathItemMatchingPathId(item, pathId);
}

export function makeSettingsItemFromLocationPathAndQuery(
  locationPathAndQuery: string,
): SettingsMenuItemTemplate | undefined {
  const pathIdForPath = findCurrentSettingsPathId(locationPathAndQuery);

  if (!pathIdForPath) {
    return undefined;
  }

  const matchFromPath = makeMatchMethodForPathId(pathIdForPath)(locationPathAndQuery);
  if (!matchFromPath) {
    return undefined;
  }

  return {
    pathId: pathIdForPath,
    parameters: matchFromPath.params,
  };
}

export function isOnPersonalRoute(locationPathAndQuery: string) {
  const itemFromLocationPathAndQuery = makeSettingsItemFromLocationPathAndQuery(locationPathAndQuery);
  const pathIdForPath = findCurrentSettingsPathId(locationPathAndQuery);

  if (!pathIdForPath || !itemFromLocationPathAndQuery) {
    return false;
  }

  const matchesPersonalRouteWithoutNamespace = personalRoutesWithoutNamespace.find((personalRouteId) =>
    isSettingsPathItemMatchingPathId(itemFromLocationPathAndQuery, personalRouteId),
  );
  if (matchesPersonalRouteWithoutNamespace) {
    return true;
  }

  const matchFromPath = makeMatchMethodForPathId(pathIdForPath)(locationPathAndQuery);
  if (!matchFromPath) {
    return false;
  }

  const {params} = matchFromPath;

  if (hasProperty(params, 'namespace') && _.isString(params.namespace)) {
    return isTeammateNamespace(params.namespace);
  }

  return false;
}

export function buildSettingsSectionExpandedKey(sectionId: string) {
  return `${CollapsedItemKeyPrefixesEnum.SETTINGS_SECTION}${sectionId}`;
}

/**
 * Build a URL to the Rule Library with a pre-filled search.
 */
export function buildRuleLibrarySearchUrl(searchQuery: string) {
  return `/settings/global/rules/library?${ruleLibrarySearchKey}=${searchQuery}`;
}
